'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { MentionItem } from '@/app/types';
import { getDownloadUrl } from '../apis';

export type ClawMentionFileType = 'image' | 'video' | 'audio' | 'file';

export interface ClawMentionCandidate {
  path: string;
  label: string;
  type?: ClawMentionFileType;
  previewUrl?: string;
}

export interface ClawMentionSearchResultItem extends MentionItem {
  path: string;
  fileType?: ClawMentionFileType;
}

interface UseClawMentionSearchOptions {
  activeClawId?: string;
  draftCandidates: ClawMentionCandidate[];
  folderCandidates: ClawMentionCandidate[];
}

const MAX_FOLDER_RESULTS = 20;

const isVisualFileType = (type?: ClawMentionFileType) => type === 'image' || type === 'video';

const normalizeText = (value: string) => value.trim().toLowerCase();

const toSearchResultItem = (candidate: ClawMentionCandidate, previewUrl?: string): ClawMentionSearchResultItem => ({
  id: candidate.path,
  label: candidate.label,
  preview: previewUrl ?? candidate.previewUrl,
  type: isVisualFileType(candidate.type) ? candidate.type : undefined,
  mediaType: candidate.type,
  path: candidate.path,
  fileType: candidate.type,
});

export const useClawMentionSearch = ({
  activeClawId,
  draftCandidates,
  folderCandidates,
}: UseClawMentionSearchOptions) => {
  const previewUrlMapRef = useRef<Record<string, string>>({});
  const lastResultMapRef = useRef<Record<string, ClawMentionSearchResultItem>>({});

  useEffect(() => {
    previewUrlMapRef.current = {};
  }, [activeClawId]);

  const searchMentionItems = useCallback(
    async (query: string): Promise<ClawMentionSearchResultItem[]> => {
      const normalizedQuery = normalizeText(query);

      const draftSeenPaths = new Set<string>();
      const draftItems = draftCandidates.flatMap((candidate) => {
        const path = candidate.path.trim();
        const label = candidate.label.trim();
        if (!path || !label || draftSeenPaths.has(path)) return [];
        draftSeenPaths.add(path);

        if (
          normalizedQuery &&
          !label.toLowerCase().includes(normalizedQuery) &&
          !path.toLowerCase().includes(normalizedQuery)
        ) {
          return [];
        }

        return [toSearchResultItem({ ...candidate, path, label })];
      });

      const draftPaths = new Set(draftItems.map((item) => item.path));
      const matchedFolderCandidates = folderCandidates
        .filter((candidate) => {
          const path = candidate.path.trim();
          const label = candidate.label.trim();
          if (!path || draftPaths.has(path)) return false;
          if (!normalizedQuery) return true;
          return label.toLowerCase().includes(normalizedQuery) || path.toLowerCase().includes(normalizedQuery);
        })
        .slice(0, MAX_FOLDER_RESULTS);

      const visualCandidates = matchedFolderCandidates.filter((candidate) => {
        const path = candidate.path.trim();
        return !!path && isVisualFileType(candidate.type) && !previewUrlMapRef.current[path];
      });

      if (activeClawId && visualCandidates.length > 0) {
        const previewResults = await Promise.all(
          visualCandidates.map(async (candidate) => {
            const path = candidate.path.trim();
            const { data, error } = await getDownloadUrl({
              query: {
                claw_id: activeClawId,
                file_path: path,
              },
            });

            if (error || !data?.download_url) return null;
            return { path, url: data.download_url };
          }),
        );

        previewResults.forEach((result) => {
          if (!result) return;
          previewUrlMapRef.current[result.path] = result.url;
        });
      }

      const folderItems = matchedFolderCandidates.map((candidate) => {
        const path = candidate.path.trim();
        return toSearchResultItem(candidate, previewUrlMapRef.current[path]);
      });

      const resultItems = [...draftItems, ...folderItems];

      // 把最近一次候选缓存起来，供组件在 onBeforeAddMention 里反查 file_path / file_type / preview。
      // useCustomMention 在挂载时只 capture 一次 onBeforeAddMention，回调里读 ref 才能拿到最新值。
      const nextResultMap: Record<string, ClawMentionSearchResultItem> = {};
      resultItems.forEach((item) => {
        if (item.path) nextResultMap[item.path] = item;
      });
      lastResultMapRef.current = nextResultMap;

      return resultItems;
    },
    [activeClawId, draftCandidates, folderCandidates],
  );

  const getResolvedItem = useCallback(
    (id: string): ClawMentionSearchResultItem | undefined => lastResultMapRef.current[id],
    [],
  );

  return {
    searchMentionItems,
    getResolvedItem,
  };
};
